
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Menu, X, MapPin, Clock, Mail, Globe } from "lucide-react";
import { companyInfo } from "../data/mockData";
import { Button } from "./ui/button";
import { useLanguage } from "../context/LanguageContext";

const Layout = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  const contactEmail = "Clslogisticactiv2018@gmail.com";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col min-h-screen font-sans">
      {/* Top Bar - Contact Info */}
      <div className="bg-primary text-primary-foreground py-2 px-4 text-sm hidden md:block">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> {t.common.zaragoza}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" /> {t.common.servicio24}
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <a href={`tel:${companyInfo.phone}`} className="hover:text-accent font-semibold flex items-center gap-1 transition-colors">
              <Phone className="w-4 h-4" /> {companyInfo.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${
          isScrolled ? "bg-background/95 backdrop-blur shadow-sm py-2" : "bg-background py-3 md:py-4"
        }`}
      >
        <div className="container mx-auto px-4 flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group z-50 relative">
            <div className="bg-primary text-primary-foreground p-2 rounded-lg group-hover:bg-primary/90 transition-colors">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-truck"
              >
                <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
                <path d="M15 18H9" />
                <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
                <circle cx="17" cy="18" r="2" />
                <circle cx="7" cy="18" r="2" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold leading-none tracking-tight">Gruas</span>
              <span className="text-sm font-semibold text-muted-foreground tracking-wide">Asistencia</span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-foreground hover:text-primary font-medium transition-colors">{t.nav.inicio}</Link>
            <a href="#services" className="text-foreground hover:text-primary font-medium transition-colors">{t.nav.servicios}</a>
            <a href="#process" className="text-foreground hover:text-primary font-medium transition-colors">{t.nav.comoFunciona}</a>
            <a href="#reviews" className="text-foreground hover:text-primary font-medium transition-colors">{t.nav.opiniones}</a>
            <a href="#faq" className="text-foreground hover:text-primary font-medium transition-colors">{t.nav.preguntas}</a>
          </nav>

          {/* Desktop CTAs + Language */}
          <div className="hidden md:flex items-center space-x-3">
            {/* Language Toggle Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="gap-2 font-semibold"
              onClick={toggleLanguage}
            >
              <Globe className="w-4 h-4" />
              {language === 'es' ? 'EN' : 'ES'}
            </Button>
            
            <Button variant="outline" className="gap-2 border-primary text-primary hover:bg-primary hover:text-white" asChild>
              <a href={`https://wa.me/${companyInfo.whatsapp.replace('+', '')}`}>
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </Button>
            <Button className="gap-2 shadow-lg hover:shadow-xl transition-all" asChild>
              <a href={`tel:${companyInfo.phone}`}>
                <Phone className="w-4 h-4" /> {t.nav.llamar24h}
              </a>
            </Button>
          </div>

          {/* Mobile Header Controls */}
          <div className="md:hidden flex items-center gap-2 z-[200] relative">
            {/* Language Toggle Mobile */}
            <Button 
              variant="ghost" 
              size="icon"
              className="w-9 h-9 font-bold text-sm"
              onClick={toggleLanguage}
            >
              {language === 'es' ? '🇬🇧' : '🇪🇸'}
            </Button>
            
            {/* Quick Call Button Mobile */}
            <Button size="icon" className="rounded-full bg-green-600 text-white shadow-md hover:bg-green-700 w-9 h-9" asChild>
              <a href={`tel:${companyInfo.phone}`}>
                <Phone className="w-4 h-4" />
              </a>
            </Button>
            
            {/* Mobile Menu Toggle */}
            <button 
              className="p-2 text-foreground focus:outline-none z-[200]" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay - Full Screen */}
        <div 
          className={`md:hidden fixed inset-0 bg-white z-[150] transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ minHeight: '100vh', height: '100%' }}
        >
          {/* Menu Header */}
          <div className="flex justify-between items-center p-4 border-b">
            <div className="flex items-center gap-2">
              <div className="bg-primary text-primary-foreground p-2 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
                  <path d="M15 18H9" />
                  <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
                  <circle cx="17" cy="18" r="2" />
                  <circle cx="7" cy="18" r="2" />
                </svg>
              </div>
              <span className="text-lg font-bold">{t.nav.menu}</span>
            </div>
            <button 
              className="p-2 text-foreground focus:outline-none" 
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Menu Content */}
          <div className="flex flex-col p-6 h-full overflow-y-auto">
            <nav className="flex flex-col space-y-1">
              <button 
                className="text-xl font-semibold py-4 border-b border-gray-100 text-left w-full active:bg-gray-100" 
                onClick={() => { 
                  setIsMobileMenuOpen(false); 
                  setTimeout(() => window.scrollTo({top: 0, behavior: 'smooth'}), 100);
                }}
              >
                {t.nav.inicio}
              </button>
              <button 
                className="text-xl font-semibold py-4 border-b border-gray-100 text-left w-full active:bg-gray-100" 
                onClick={() => { 
                  setIsMobileMenuOpen(false); 
                  setTimeout(() => document.getElementById('services')?.scrollIntoView({behavior: 'smooth'}), 100);
                }}
              >
                {t.nav.servicios}
              </button>
              <button 
                className="text-xl font-semibold py-4 border-b border-gray-100 text-left w-full active:bg-gray-100" 
                onClick={() => { 
                  setIsMobileMenuOpen(false); 
                  setTimeout(() => document.getElementById('process')?.scrollIntoView({behavior: 'smooth'}), 100);
                }}
              >
                {t.nav.comoFunciona}
              </button>
              <button 
                className="text-xl font-semibold py-4 border-b border-gray-100 text-left w-full active:bg-gray-100" 
                onClick={() => { 
                  setIsMobileMenuOpen(false); 
                  setTimeout(() => document.getElementById('reviews')?.scrollIntoView({behavior: 'smooth'}), 100);
                }}
              >
                {t.nav.opiniones}
              </button>
              <button 
                className="text-xl font-semibold py-4 border-b border-gray-100 text-left w-full active:bg-gray-100" 
                onClick={() => { 
                  setIsMobileMenuOpen(false); 
                  setTimeout(() => document.getElementById('faq')?.scrollIntoView({behavior: 'smooth'}), 100);
                }}
              >
                {t.nav.preguntas}
              </button>
            </nav>

            {/* Contact Section in Mobile Menu */}
            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                {t.nav.contacto}
              </h3>
              <a 
                href={`mailto:${contactEmail}`} 
                className="text-primary hover:underline break-all text-sm"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {contactEmail}
              </a>
            </div>
            
            <div className="flex flex-col gap-4 mt-6 pb-32">
              <div className="flex items-center gap-3 text-muted-foreground mb-2">
                <Clock className="w-5 h-5 text-primary" />
                <span>{t.mobile.disponible}</span>
              </div>
              
              <Button className="w-full gap-2 h-14 text-lg shadow-lg" size="lg" asChild>
                <a href={`tel:${companyInfo.phone}`} onClick={() => setIsMobileMenuOpen(false)}>
                  <Phone className="w-5 h-5" /> {t.mobile.llamarAhora}
                </a>
              </Button>
              <Button variant="secondary" className="w-full gap-2 h-14 text-lg border border-border" size="lg" asChild>
                <a href={`https://wa.me/${companyInfo.whatsapp.replace('+', '')}`} onClick={() => setIsMobileMenuOpen(false)}>
                  <MessageCircle className="w-5 h-5 text-green-600" /> WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-secondary text-secondary-foreground pt-12 pb-24 md:pb-6">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-primary p-2 rounded text-white">
                    <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-truck"
                  >
                    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
                    <path d="M15 18H9" />
                    <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
                    <circle cx="17" cy="18" r="2" />
                    <circle cx="7" cy="18" r="2" />
                  </svg>
                </div>
                <span className="text-xl font-bold">Gruas Asistencia</span>
              </div>
              <p className="text-slate-400 mb-4">
                {t.footer.description}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">{t.footer.contactTitle}</h3>
              <ul className="space-y-3">
                <li>
                  <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Phone className="w-4 h-4 text-primary" /> {companyInfo.phone}
                  </a>
                </li>
                <li>
                  <a href={`https://wa.me/${companyInfo.whatsapp.replace('+', '')}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                    <MessageCircle className="w-4 h-4 text-primary" /> {companyInfo.whatsapp}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${contactEmail}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Mail className="w-4 h-4 text-primary" /> {contactEmail}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" /> {companyInfo.location}
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">{t.footer.linksTitle}</h3>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#services" className="hover:text-white transition-colors">{t.nav.servicios}</a></li>
                <li><a href="#process" className="hover:text-white transition-colors">{t.nav.comoFunciona}</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">{t.nav.preguntas}</a></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t.footer.privacyPolicy}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Gruas Asistencia. {t.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
